import { useCallback, useMemo } from 'react';

import { PaymentMethod } from '@stripe/stripe-js';
import { CircleCheckIcon, EllipsisVerticalIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_CONFIRMATION } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { Customer } from '@/modules/billing/model';
import { popModal, pushModal } from '@/redux/modals/actions';

const isDefault = (paymentMethod: PaymentMethod, customer: Customer | undefined) => {
  return customer?.invoice_settings.default_payment_method === paymentMethod.id;
};

export const PaymentMethodsTable = () => {
  const { t } = useTranslation();
  const { paymentMethods, customer, updateCustomerAsync, detachPaymentMethodAsync } = useBilling();

  const cardTypeLabels: Record<string, string> = {
    credit: t('billing.payment_card_credit'),
    debit: t('billing.payment_card_debit'),
    prepaid: t('billing.payment_card_prepaid'),
    unknown: t('billing.payment_card_unknown'),
  };
  const dispatch = useDispatch();

  const handleDelete = useCallback(
    (pmId: string) => {
      const paymentMethod = paymentMethods?.find((pm) => pm.id === pmId);
      if (!paymentMethod) {
        return;
      }
      const makeNextOneDefault = isDefault(paymentMethod, customer);
      dispatch(
        pushModal(MODAL_CONFIRMATION, {
          title: t('billing.payment_remove_card'),
          primaryButtonText: t('billing.payment_card_delete_confirm'),
          subtitle: t('billing.payment_remove_card_subtitle', {
            0: paymentMethod.card?.brand,
            1: paymentMethod.card?.last4,
          }),
          onConfirm: async () => {
            try {
              await detachPaymentMethodAsync(paymentMethod.id);
            } catch (error) {
              toast.error(error?.message || t('billing.payment_card_delete_error'));
              dispatch(popModal());
              return;
            }
            toast.success(t('billing.payment_card_deleted'));
            if (makeNextOneDefault) {
              const nextDefault = paymentMethods?.find((p) => p.id !== paymentMethod.id);
              if (nextDefault) {
                await updateCustomerAsync({
                  invoice_settings: { default_payment_method: nextDefault.id },
                });
              }
            }
            dispatch(popModal());
          },
        })
      );
    },
    [customer, detachPaymentMethodAsync, dispatch, paymentMethods, updateCustomerAsync, t]
  );

  const handleMakeDefault = useCallback(
    async (pmId: string) => {
      const paymentMethod = paymentMethods?.find((pm) => pm.id === pmId);
      if (!paymentMethod) {
        return;
      }
      dispatch(
        pushModal(MODAL_CONFIRMATION, {
          title: t('billing.payment_card_change_default'),
          primaryButtonText: t('billing.payment_card_change_default_confirm'),
          subtitle: t('billing.payment_card_change_default_subtitle', {
            0: paymentMethod.card?.brand,
            1: paymentMethod.card?.last4,
          }),
          onConfirm: async () => {
            await updateCustomerAsync({ invoice_settings: { default_payment_method: pmId } });
            dispatch(popModal());
          },
        })
      );
    },
    [paymentMethods, dispatch, updateCustomerAsync, t]
  );

  const methods = useMemo(() => {
    if (!paymentMethods?.length) {
      return [];
    }
    return paymentMethods
      .filter((pm) => pm.card)
      .map((pm) => ({
        brand: pm.card?.brand,
        isDefault: isDefault(pm, customer),
        type: pm.card?.funding ?? '',
        last4: pm.card?.last4,
        exp: `${pm.card?.exp_month}/${pm.card?.exp_year}`,
        id: pm.id,
      }));
  }, [paymentMethods, customer]);

  if (!methods.length) {
    return null;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10" />
          <TableHead>{t('billing.payment_card_columns.brand')}</TableHead>
          <TableHead>{t('billing.payment_card_columns.type')}</TableHead>
          <TableHead>{t('billing.payment_card_columns.last_4_num')}</TableHead>
          <TableHead>{t('billing.payment_card_columns.exp')}</TableHead>
          <TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {methods.map((method) => (
          <TableRow key={method.id}>
            <TableCell className="text-center">
              {method.isDefault && (
                <InfoTooltip content={t('billing.default_card')} side="top" showArrow>
                  <CircleCheckIcon size={16} className="mx-auto text-success" />
                </InfoTooltip>
              )}
            </TableCell>
            <TableCell className="capitalize">{method.brand}</TableCell>
            <TableCell>{cardTypeLabels[method.type]}</TableCell>
            <TableCell className="text-muted-foreground">...{method.last4}</TableCell>
            <TableCell className="text-muted-foreground">{method.exp}</TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost-neutral" size="icon-sm">
                    <EllipsisVerticalIcon size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!method.isDefault && (
                    <DropdownMenuItem onClick={() => handleMakeDefault(method.id)}>
                      <CircleCheckIcon size={16} className="text-muted-foreground" />
                      {t('billing.set_as_default')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={() => handleDelete(method.id)} variant="destructive">
                    <Trash2Icon size={16} />
                    {`${t('common.delete')}…`}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
